import { DownloadIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

import { IOneMstsTest } from "../../../@types/admin/AdminOneMsts";
import { TableSorted } from "../../../components/TableSorted";
import { useFileDownload } from "../../../hooks/mutations/admin/fileTransfer";
import { useOneMstsTests } from "../../../hooks/queries/admin/oneMsts";
import { oneMstsExportUrl } from "../../../services/admin/oneMstsService";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  RowAction,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";

const SEARCH_FIELDS: (keyof IOneMstsTest)[] = [
  "id",
  "patientId",
  "status",
  "createdDate",
];

/** Retool named the export `1MSTS-PatientId-160-01-08-2026.xlsx`. */
function exportFilename(test: IOneMstsTest): string {
  const date = dayjs(new Date(test.createdDate)).format("DD-MM-YYYY");
  return `1MSTS-PatientId-${test.patientId}-${date}.xlsx`;
}

/**
 * A completed test is the normal case; anything else (cancelled, abandoned,
 * in progress) is what an operator is scanning for.
 */
function statusTone(status: string): "active" | "inactive" {
  return status?.toLowerCase() === "completed" ? "active" : "inactive";
}

/**
 * 1MSTS (One Minute Sit To Stand) admin page (EN-5159), migrated from the
 * Retool OneMinuteSitToStandTest app. Lists the tests and exports one as the
 * Excel workbook the endpoint builds.
 *
 * The export goes through the shared `useFileDownload` (EN-5148) rather than
 * Retool's inline script: `GET /Retool/OneMsts/{id}/Export` returns a real
 * file with a Content-Disposition name, so the base64 envelope the Retool
 * event chain decoded was a Retool transport detail, not the API contract.
 * The Retool-style name is passed as the fallback for the case where the
 * header doesn't survive a proxy.
 *
 * The list endpoint also returns each test's pre/post pulse-ox, Borg answers
 * and continuous SpO2 trace. Retool showed none of it and neither does this
 * page — the export is where that detail is read. A results view is a
 * separate piece of work if one is ever wanted.
 */
export function AdminOneMstsTests() {
  document.title = "1MSTS tests - Admin";

  const { oneMstsTests, isLoading, isError } = useOneMstsTests();
  const { downloadFile } = useFileDownload();
  const [exportingId, setExportingId] = useState<string | null>(null);

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({ data: oneMstsTests, searchFields: SEARCH_FIELDS });

  const handleExport = async (test: IOneMstsTest) => {
    setExportingId(test.id);
    try {
      await downloadFile(
        { url: oneMstsExportUrl(test.id) },
        {
          fallbackFilename: exportFilename(test),
          errorMessage: "The test export failed. Try again.",
        }
      );
    } finally {
      setExportingId(null);
    }
  };

  const columns: ColumnDef<IOneMstsTest, string>[] = useMemo(
    () => [
      { accessorKey: "id", header: "ID", enableSorting: true },
      {
        accessorKey: "createdDate",
        header: "Created date",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.createdDate, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "patientId",
        header: "Patient ID",
        cell: ({ row }) => <span>{row.original.patientId}</span>,
        enableSorting: true,
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (test) => test.status,
        cell: ({ row }) => (
          <StatusBadge status={statusTone(row.original.status)}>
            {row.original.status}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowAction
            label={
              exportingId === row.original.id ? "Exporting..." : "Export test"
            }
            icon={<DownloadIcon />}
            disabled={exportingId !== null}
            onClick={() => {
              handleExport(row.original);
            }}
          />
        ),
      },
    ],
    [exportingId]
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>1MSTS tests</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by test ID, patient ID, or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the 1MSTS tests. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-one-msts-tests"]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "1msts-tests",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
